// NewSidebar/NewSideBar.jsx
import React, { useEffect, useState, useMemo } from "react";
import menuIconSvg from "../../asset/Icons/menuIcon.svg";
import "./style/SideBar.css";
import SearchMenu from "../../General/SearchMenu/SearchMenu";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import Item from "./NewSidebar/SidebarItem";       // همان Item فعلی شما
import Submenu from "./NewSidebar/SidebarSubmenu";  // همان Submenu فعلی شما
import iconMap from "./NewSidebar/iconMap";
import usePermissionEngine from "./NewSidebar/usePermissionEngine";
import sidebarMenuData from "./NewSidebar/sidebarMenu.json";
import useDeviceType from "./Hooks/useDeviceType";
import { useLocation } from "react-router-dom";
import LogOutIcon from "./Icon/LogOutIcon";

const searchMenuItems = (items, query) => {
  const q = query.trim().toLowerCase();

  if (!q) {
    return items;
  }

  return items.reduce((result, item) => {
    const title = item.title?.trim().toLowerCase() || "";

    // تبدیل عنوان و سرچ به کلمات مستقل
    const titleWords = title.split(/\s+/);
    const queryWords = q.split(/\s+/);

    // تمام کلمات جستجو باید به صورت کامل
    // در عنوان وجود داشته باشند
    const parentMatch = queryWords.every((queryWord) =>
      titleWords.includes(queryWord)
    );

    if (parentMatch) {
      result.push(item);
      return result;
    }

    // جستجوی recursive داخل children
    if (item.children?.length) {
      const children = searchMenuItems(
        item.children,
        query
      );

      if (children.length > 0) {
        result.push({
          ...item,
          children,
        });
      }
    }

    return result;
  }, []);
};

function NewSideBar({ mobileOpen, onClose }) {
  const appSetting = useSelector(state => state.general.appSetting);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();
  const [prevPath, setPrevPath] = useState(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPath) onClose?.();
    setPrevPath(location.pathname);
  }, [location.pathname]);



  /* ─── Sidebar toggle (desktop) ─── */
  const [sidebar, setSidebar] = useState(true);
  const toggleSideBar = () => setSidebar((prev) => !prev);

  /* ─── Selected state ─── */
  const [selected, setSelected] = useState("داشبورد");

  /* ─── Device type ─── */
  const deviceType = useDeviceType();

  /* ═══════════════════════════════════════════
     ★ Permission Engine — فیلتر منو
     ═══════════════════════════════════════════ */
  const filteredMenu = usePermissionEngine(sidebarMenuData.menu);

  const searchedMenu = useMemo(() => {
    return searchMenuItems(
      filteredMenu,
      searchQuery
    );
  }, [filteredMenu, searchQuery]);







  /* ─── رندر بازگشتی آیتم‌ها ─── */
  const renderMenuItem = (item) => {
    if (item.children && item.children.length > 0) {
      return (
        <Submenu
          key={item.key}
          selected={selected}
          sidebar={sidebar}
          title={item.title}
          keyEn={item.keyEn || item.key}
          icon={iconMap[item.icon] || null}
        >
          {item.children.map(renderMenuItem)}
        </Submenu>
      );
    }

    return (
      <Item
        key={item.key}
        selected={selected}
        title={item.title}
        setSelected={setSelected}
        to={item.to}
        icon={iconMap[item.icon] || null}
        smallpadding={item.smallpadding}
        submenu={item.submenu}
        sidebar={sidebar}
      />
    );
  };

  /* ─── Logout Item (همیشه نمایش داده می‌شود) ─── */
  const logoutItem = (
    <Item
      key="logout"
      selected={selected}
      title="خروج"
      setSelected={setSelected}
      icon={<LogOutIcon />}
      smallpadding={true}
      sidebar={sidebar}
    />
  );

  /* ══════════════════════════════════════════════
     📱 MOBILE SIDEBAR
     ══════════════════════════════════════════════ */
  if (deviceType === "mobile") {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "80%",
            maxWidth: "330px",
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box
          className="sidebar-cls"
          sx={{
            width: "100%",
            borderRadius: "24px 0 0 24px",
            height: "100vh",
            overflowY: "auto",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>
          <Box sx={{ mt: 2 }}>
            {filteredMenu.map(renderMenuItem)}
            {logoutItem}
          </Box>
        </Box>
      </Drawer>
    );
  }

  /* ══════════════════════════════════════════════
     🖥️ DESKTOP SIDEBAR
     ══════════════════════════════════════════════ */
  return (
    <div
      style={{ width: sidebar ? "15rem" : "6rem" }}
      className="sidebar-cls"
    >
      <img
        src={menuIconSvg}
        onClick={toggleSideBar}
        alt="icon"
        className="menu-icon"
      />
      <ul
        style={{
          width: "100%",
          padding: "15px",
          display: "flex",
          flexDirection: "column",
          alignItems: sidebar ? "flex-start" : "center",
        }}
      >

        <AnimatePresence mode="wait">
          {(appSetting.mainMenuSeacrh && sidebar) && (
            <motion.div
              initial={{
                opacity: 0,
                y: -8,
                height: 0,
              }}
              animate={{
                opacity: 1,
                y: 0,
                height: 'auto',
              }}
              exit={{
                opacity: 0,
                y: -8,
                height: 0,
              }}
              transition={{
                duration: 0.2,
                ease: 'easeOut',
              }}
              style={{
                overflow: 'hidden',
              }}
            >
              <SearchMenu
                value={searchQuery}
                onSearch={setSearchQuery}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {searchedMenu.map((item) => (
          <React.Fragment
            key={`${item.key}`}
          >
            {renderMenuItem(item)}

          </React.Fragment>
        ))}
      </ul>
    </div>
  );
}

export default NewSideBar;
